import { createSlice } from '@reduxjs/toolkit';

import {
  apiGetNewsItems,
  apiGetFavouritedNewsItems,
  apiFavouriteNewsItem,
  apiUnfavouriteNewsItem,
  apiRepostNewsItem,
  apiUnrepostNewsItem,
} from '@/mastodon/api/news';
import type { ApiNewsItemJSON } from '@/mastodon/api_types/news';
import { createAppAsyncThunk } from '@/mastodon/store/typed_functions';

// Matches Api::V1::Timelines::NewsController's DEFAULT_STATUSES_LIMIT — used
// only to guess whether another page might exist (a short page means we hit
// the end), since this fork's simple apiRequestGet helper doesn't expose the
// pagination Link header the way the legacy timeline actions do.
const NEWS_ITEMS_LIMIT = 20;

interface NewsItemsState {
  items: Record<string, ApiNewsItemJSON>;
  ids: string[];
  isLoading: boolean;
  hasMore: boolean;
  likedIds: string[];
  likedIsLoading: boolean;
  likedHasMore: boolean;
}

const initialState: NewsItemsState = {
  items: {},
  ids: [],
  isLoading: false,
  hasMore: true,
  likedIds: [],
  likedIsLoading: false,
  likedHasMore: true,
};

export const fetchNewsItems = createAppAsyncThunk(
  'newsItems/fetch',
  (params: { maxId?: string; newsFeedIds?: string[] }) =>
    apiGetNewsItems({
      ...(params.maxId ? { max_id: params.maxId } : {}),
      ...(params.newsFeedIds ? { news_feed_id: params.newsFeedIds } : {}),
    }),
);

export const fetchFavouritedNewsItems = createAppAsyncThunk(
  'newsItems/fetchFavourited',
  (params: { maxId?: string }) =>
    apiGetFavouritedNewsItems(
      params.maxId ? { max_id: params.maxId } : undefined,
    ),
);

export const favouriteNewsItem = createAppAsyncThunk(
  'newsItems/favourite',
  (id: string) => apiFavouriteNewsItem(id),
);

export const unfavouriteNewsItem = createAppAsyncThunk(
  'newsItems/unfavourite',
  (id: string) => apiUnfavouriteNewsItem(id),
);

export const repostNewsItem = createAppAsyncThunk(
  'newsItems/repost',
  ({ id, text }: { id: string; text?: string }) => apiRepostNewsItem(id, text),
);

export const unrepostNewsItem = createAppAsyncThunk(
  'newsItems/unrepost',
  (id: string) => apiUnrepostNewsItem(id),
);

const newsItemsSlice = createSlice({
  name: 'newsItems',
  initialState,
  reducers: {
    // Clears the main (non-"liked") list so changing the Filters > Sources
    // selection re-renders from a clean, correctly-filtered first page
    // instead of mixing in whatever was already loaded under the old
    // filter. `items` (the id -> item cache) is left alone -- harmless to
    // keep entries around that the cleared `ids` list no longer renders.
    resetNewsItems(state) {
      state.ids = [];
      state.hasMore = true;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchNewsItems.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchNewsItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasMore = action.payload.length >= NEWS_ITEMS_LIMIT;

        action.payload.forEach((item) => {
          state.items[item.id] = item;
          if (!state.ids.includes(item.id)) {
            state.ids.push(item.id);
          }
        });
      })
      .addCase(fetchNewsItems.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchFavouritedNewsItems.pending, (state) => {
        state.likedIsLoading = true;
      })
      .addCase(fetchFavouritedNewsItems.fulfilled, (state, action) => {
        state.likedIsLoading = false;
        state.likedHasMore = action.payload.length >= NEWS_ITEMS_LIMIT;

        action.payload.forEach((item) => {
          state.items[item.id] = item;
          if (!state.likedIds.includes(item.id)) {
            state.likedIds.push(item.id);
          }
        });
      })
      .addCase(fetchFavouritedNewsItems.rejected, (state) => {
        state.likedIsLoading = false;
      })
      .addCase(favouriteNewsItem.fulfilled, (state, action) => {
        state.items[action.payload.id] = action.payload;
        if (!state.likedIds.includes(action.payload.id)) {
          state.likedIds.unshift(action.payload.id);
        }
      })
      .addCase(unfavouriteNewsItem.fulfilled, (state, action) => {
        state.items[action.payload.id] = action.payload;
        state.likedIds = state.likedIds.filter(
          (id) => id !== action.payload.id,
        );
      })
      .addCase(repostNewsItem.fulfilled, (state, action) => {
        state.items[action.payload.id] = action.payload;
      })
      .addCase(unrepostNewsItem.fulfilled, (state, action) => {
        state.items[action.payload.id] = action.payload;
      });
  },
});

export const { resetNewsItems } = newsItemsSlice.actions;

export const newsItems = newsItemsSlice.reducer;
